import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Check,
  ChevronLeft,
  ClipboardPlus,
  FileText,
  Gauge,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Trash2,
  Watch,
  X,
} from 'lucide-react';
import { fitnessApi, healthModulesApi } from '../api';
import HealthRecordDialog, { type HealthRecordKind, type VitalRecordKind } from './HealthRecordDialog';
import VitalHistoryDialog from './VitalHistoryDialog';
import './WellnessCenter.css';

type HealthWorkspace = 'overview' | 'metrics' | 'nutrition' | 'fitness' | 'reports';
type CoreRecordKind = HealthRecordKind;

interface Props {
  onClose: () => void;
  onOpenHealth: (tab?: 'overview' | 'metrics' | 'reports') => void;
  onOpenNutrition: () => void;
  onOpenMissions: () => void;
  onOpenFitness?: () => void;
}

interface PersonalMetric {
  id: number;
  name: string;
  unit: string;
  category: string;
  category_label: string;
  reference_min: number | null;
  reference_max: number | null;
  target_text: string;
  latest: {
    id: number;
    value: number;
    measured_at: string;
    note: string;
    source: string;
    status: string;
    status_label: string;
  } | null;
  trend: Array<{ date: string; value: number }>;
  record_count: number;
}

interface MetricRow {
  key: string;
  name: string;
  unit: string;
  value: string;
  target: string;
  change: string;
  recordedAt: string;
  dataSource: 'manual' | 'report' | 'missing';
  sourceLabel: string;
  status: 'normal' | 'attention' | 'missing' | 'tracked';
  statusLabel: string;
  trend: number[];
  coreKind?: CoreRecordKind;
  personalMetric?: PersonalMetric;
}

export type WellnessWorkspace = HealthWorkspace;

const WORKSPACE_TABS: Array<{ key: HealthWorkspace; label: string }> = [
  { key: 'overview', label: '总览' },
  { key: 'metrics', label: '指标' },
  { key: 'nutrition', label: '营养' },
  { key: 'fitness', label: '运动' },
  { key: 'reports', label: '报告' },
];

const CORE_RECORD_OPTIONS: Array<{ kind: CoreRecordKind; label: string }> = [
  { kind: 'bp', label: '血压' },
  { kind: 'weight', label: '体重与体成分' },
  { kind: 'sugar', label: '血糖' },
  { kind: 'hr', label: '心率' },
];

export function WellnessTabBar({ active, onNavigate }: { active: HealthWorkspace; onNavigate: (workspace: HealthWorkspace) => void }) {
  return (
    <nav className="wl-workspace-tabs" aria-label="身体管理工作区">
      {WORKSPACE_TABS.map((tab) => (
        <button key={tab.key} type="button" className={tab.key === active ? 'active' : ''} onClick={() => onNavigate(tab.key)}>
          {tab.label}
        </button>
      ))}
    </nav>
  );
}

function formatTime(value?: string): string {
  if (!value) return '暂无数据';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '暂无数据';
  const today = new Date();
  const time = date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false });
  return date.toDateString() === today.toDateString()
    ? `今天 ${time}`
    : `${date.getMonth() + 1}-${date.getDate()} ${time}`;
}

function formatSyncTime(value?: string): string {
  return value ? `同步于 ${formatTime(value)}` : '等待健康数据';
}

function formatSource(record?: any): MetricRow['dataSource'] {
  if (!record) return 'missing';
  return record.source === 'report' ? 'report' : 'manual';
}

function formatSourceLabel(source: MetricRow['dataSource']): string {
  if (source === 'report') return '体检报告';
  if (source === 'manual') return '手动记录';
  return '暂无来源';
}

function formatReportMetricValue(item: any): string {
  const value = String(item?.value || '--').trim();
  const unit = String(item?.unit || '').trim();
  if (!unit || value.toLowerCase().includes(unit.toLowerCase())) return value;
  return `${value} ${unit}`;
}

function dataAgeDays(value?: string): number | null {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : Math.max(0, Math.floor((Date.now() - timestamp) / 86400000));
}

function Sparkline({ values, tone = 'blue' }: { values: number[]; tone?: 'blue' | 'green' | 'amber' }) {
  if (values.length < 2) return <span className="wl-no-trend">--</span>;
  const width = 88;
  const height = 34;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = max - min || 1;
  const points = values.map((value, index) => {
    const x = (index / (values.length - 1)) * width;
    const y = height - 4 - ((value - min) / spread) * (height - 8);
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg className={`wl-sparkline ${tone}`} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline points={points} fill="none" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function StatusMark({ status }: { status: MetricRow['status'] }) {
  return <span className={`wl-status-dot ${status}`} aria-hidden="true" />;
}

function PersonalRecordDialog({ metric, onClose, onSaved }: { metric: PersonalMetric; onClose: () => void; onSaved: () => Promise<void> }) {
  const [value, setValue] = useState('');
  const [measuredAt, setMeasuredAt] = useState(new Date().toISOString().slice(0, 16));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await healthModulesApi.addPersonalMetricRecord(metric.id, {
        value: Number(value),
        measured_at: measuredAt ? new Date(measuredAt).toISOString() : undefined,
        note,
      });
      await onSaved();
      onClose();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.detail || '保存失败，请检查填写内容');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wl-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="wl-dialog wl-dialog-compact" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
        <header className="wl-dialog-header">
          <div><span className="wl-eyebrow">个人指标</span><h2>记录{metric.name}</h2></div>
          <button type="button" className="wl-icon-button" onClick={onClose} aria-label="关闭"><X size={17} /></button>
        </header>
        <form onSubmit={submit}>
          <div className="wl-form-grid">
            <label className="wl-form-wide">数值<input autoFocus required type="number" step="any" value={value} onChange={(event) => setValue(event.target.value)} /><small>{metric.unit}</small></label>
            <label className="wl-form-wide">测量时间<input type="datetime-local" value={measuredAt} onChange={(event) => setMeasuredAt(event.target.value)} /></label>
            <label className="wl-form-wide">备注<textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} /></label>
          </div>
          {error && <p className="wl-form-error">{error}</p>}
          <footer className="wl-dialog-actions">
            <button type="button" className="wl-button secondary" onClick={onClose}>取消</button>
            <button type="submit" className="wl-button primary" disabled={saving}>{saving ? '保存中...' : '保存记录'}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}

function AddMetricDialog({
  templates,
  onClose,
  onSaved,
}: {
  templates: any[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [form, setForm] = useState<Record<string, string>>({
    name: '',
    unit: '',
    category: 'vital',
    reference_min: '',
    reference_max: '',
    target_text: '',
    initial_value: '',
    measured_at: new Date().toISOString().slice(0, 16),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const setField = (name: string, value: string) => setForm((current) => ({ ...current, [name]: value }));

  const applyTemplate = (template: any) => {
    setForm((current) => ({
      ...current,
      name: template.name || '',
      unit: template.unit || '',
      category: template.category || 'other',
      reference_min: template.reference_min == null ? '' : String(template.reference_min),
      reference_max: template.reference_max == null ? '' : String(template.reference_max),
      target_text: template.target_text || '',
    }));
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await healthModulesApi.addPersonalMetric({
        name: form.name.trim(),
        unit: form.unit.trim(),
        category: form.category,
        reference_min: form.reference_min ? Number(form.reference_min) : undefined,
        reference_max: form.reference_max ? Number(form.reference_max) : undefined,
        target_text: form.target_text.trim(),
        initial_value: form.initial_value ? Number(form.initial_value) : undefined,
        measured_at: form.measured_at ? new Date(form.measured_at).toISOString() : undefined,
      });
      await onSaved();
      onClose();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.detail || '新增失败，请检查填写内容');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wl-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="wl-dialog" role="dialog" aria-modal="true" aria-labelledby="wl-add-metric-title" onMouseDown={(event) => event.stopPropagation()}>
        <header className="wl-dialog-header">
          <div><span className="wl-eyebrow">扩展健康档案</span><h2 id="wl-add-metric-title">新增个人指标</h2></div>
          <button type="button" className="wl-icon-button" onClick={onClose} aria-label="关闭"><X size={17} /></button>
        </header>
        <form onSubmit={submit}>
          <div className="wl-template-list">
            {templates.map((template) => (
              <button key={template.name} type="button" onClick={() => applyTemplate(template)}>{template.name}</button>
            ))}
          </div>
          <div className="wl-form-grid">
            <label>指标名称<input required value={form.name} onChange={(event) => setField('name', event.target.value)} placeholder="例如：血氧饱和度" /></label>
            <label>单位<input value={form.unit} onChange={(event) => setField('unit', event.target.value)} placeholder="例如：%、cm、小时" /></label>
            <label>分类<select value={form.category} onChange={(event) => setField('category', event.target.value)}><option value="vital">生命体征</option><option value="body">身体成分</option><option value="activity">活动与恢复</option><option value="lab">化验指标</option><option value="other">其他</option></select></label>
            <label>初始值<input type="number" step="any" value={form.initial_value} onChange={(event) => setField('initial_value', event.target.value)} /></label>
            <label>参考下限<input type="number" step="any" value={form.reference_min} onChange={(event) => setField('reference_min', event.target.value)} /></label>
            <label>参考上限<input type="number" step="any" value={form.reference_max} onChange={(event) => setField('reference_max', event.target.value)} /></label>
            <label className="wl-form-wide">目标说明<input value={form.target_text} onChange={(event) => setField('target_text', event.target.value)} placeholder="可选：例如每日 6000-10000 步" /></label>
            {form.initial_value && <label className="wl-form-wide">首次记录时间<input type="datetime-local" value={form.measured_at} onChange={(event) => setField('measured_at', event.target.value)} /></label>}
          </div>
          {error && <p className="wl-form-error">{error}</p>}
          <footer className="wl-dialog-actions">
            <button type="button" className="wl-button secondary" onClick={onClose}>取消</button>
            <button type="submit" className="wl-button primary" disabled={saving}>{saving ? '创建中...' : '创建指标'}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}

function WellnessCenter({ onClose, onOpenHealth, onOpenNutrition, onOpenMissions, onOpenFitness }: Props) {
  const [loading, setLoading] = useState(true);
  const [wellness, setWellness] = useState<any>(null);
  const [health, setHealth] = useState<any>(null);
  const [period, setPeriod] = useState<'7' | '30'>('30');
  const [trends, setTrends] = useState<{ bp: number[]; weight: number[]; sugar: number[]; hr: number[] }>({ bp: [], weight: [], sugar: [], hr: [] });
  const [recordKind, setRecordKind] = useState<CoreRecordKind | null>(null);
  const [historyKind, setHistoryKind] = useState<VitalRecordKind | null>(null);
  const [personalRecordMetric, setPersonalRecordMetric] = useState<PersonalMetric | null>(null);
  const [recordMenuOpen, setRecordMenuOpen] = useState(false);
  const [addMetricOpen, setAddMetricOpen] = useState(false);
  const [scoreBusy, setScoreBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [activeWorkspace, setActiveWorkspace] = useState<HealthWorkspace>('overview');
  const [templates, setTemplates] = useState<any[]>([]);

  const load = useCallback(async () => {
    setLoadError('');
    const [wellnessResult, healthResult, bpTrendResult, weightTrendResult, sugarTrendResult, hrRecordsResult, customResult] = await Promise.allSettled([
      fitnessApi.getWellness(),
      healthModulesApi.getDashboard(),
      healthModulesApi.getBpTrend(Number(period)),
      healthModulesApi.getWeightTrend(Number(period)),
      healthModulesApi.getSugarTrend(Number(period)),
      healthModulesApi.getHrRecords(Number(period)),
      healthModulesApi.getPersonalMetrics(Number(period)),
    ]);
    if (wellnessResult.status === 'fulfilled') setWellness(wellnessResult.value.data);
    if (healthResult.status === 'fulfilled') setHealth(healthResult.value.data);
    if (customResult.status === 'fulfilled') setTemplates(customResult.value.data?.templates || []);
    setTrends({
      bp: bpTrendResult.status === 'fulfilled' ? (bpTrendResult.value.data?.data || []).map((item: any) => Number(item.systolic)).filter(Number.isFinite).slice(-8) : [],
      weight: weightTrendResult.status === 'fulfilled' ? (weightTrendResult.value.data?.data || []).map((item: any) => Number(item.weight)).filter(Number.isFinite).slice(-8) : [],
      sugar: sugarTrendResult.status === 'fulfilled' ? (sugarTrendResult.value.data?.data || []).map((item: any) => Number(item.value)).filter(Number.isFinite).slice(-8) : [],
      hr: hrRecordsResult.status === 'fulfilled'
        ? (Array.isArray(hrRecordsResult.value.data) ? [...hrRecordsResult.value.data].reverse() : []).map((item: any) => Number(item.heart_rate)).filter(Number.isFinite).slice(-8)
        : [],
    });
    if (wellnessResult.status === 'rejected' && healthResult.status === 'rejected') {
      setLoadError('身体数据暂时无法加载，请稍后重试');
    }
    setLoading(false);
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const latestTimestamp = useMemo(() => {
    const values = [
      health?.latest_bp?.measured_at,
      health?.latest_weight?.measured_at,
      health?.latest_sugar?.measured_at,
      health?.latest_hr?.measured_at,
      ...(health?.personal_metrics || []).map((metric: PersonalMetric) => metric.latest?.measured_at),
      health?.score?.calculated_at,
    ].filter(Boolean).sort();
    return values[values.length - 1];
  }, [health]);

  const metricRows = useMemo<MetricRow[]>(() => {
    const bp = health?.latest_bp;
    const weight = health?.latest_weight;
    const sugar = health?.latest_sugar;
    const hr = health?.latest_hr;
    const rows: MetricRow[] = [
      {
        key: 'bp', name: '血压', unit: 'mmHg', coreKind: 'bp',
        value: bp ? `${bp.systolic}/${bp.diastolic}` : '--', target: '90-120 / 60-80',
        change: bp ? bp.status_label : '待记录', recordedAt: formatTime(bp?.measured_at),
        dataSource: formatSource(bp), sourceLabel: formatSourceLabel(formatSource(bp)),
        status: !bp ? 'missing' : bp.status === 'normal' ? 'normal' : 'attention',
        statusLabel: !bp ? '无数据' : bp.status_label, trend: trends.bp,
      },
      {
        key: 'weight', name: '体重 / BMI', unit: 'kg / kg/m²', coreKind: 'weight',
        value: weight ? `${Number(weight.weight).toFixed(1)}${weight.bmi ? ` / ${Number(weight.bmi).toFixed(1)}` : ''}` : '--',
        target: 'BMI 18.5-24.0', change: weight ? weight.bmi_status_label : '待记录',
        recordedAt: formatTime(weight?.measured_at), dataSource: formatSource(weight), sourceLabel: formatSourceLabel(formatSource(weight)),
        status: !weight ? 'missing' : weight.bmi_status === 'normal' ? 'normal' : 'attention',
        statusLabel: !weight ? '无数据' : weight.bmi_status_label, trend: trends.weight,
      },
      {
        key: 'sugar', name: '血糖', unit: 'mmol/L', coreKind: 'sugar',
        value: sugar ? Number(sugar.value).toFixed(1) : '--', target: '空腹 3.9-6.1',
        change: sugar ? sugar.status_label : '待记录', recordedAt: formatTime(sugar?.measured_at),
        dataSource: formatSource(sugar), sourceLabel: formatSourceLabel(formatSource(sugar)),
        status: !sugar ? 'missing' : sugar.status === 'normal' ? 'normal' : 'attention',
        statusLabel: !sugar ? '无数据' : sugar.status_label, trend: trends.sugar,
      },
      {
        key: 'hr', name: '心率', unit: '次/分', coreKind: 'hr',
        value: hr ? String(hr.heart_rate) : '--', target: '静息 60-100',
        change: hr ? hr.status_label : '待记录', recordedAt: formatTime(hr?.measured_at),
        dataSource: formatSource(hr), sourceLabel: formatSourceLabel(formatSource(hr)),
        status: !hr ? 'missing' : hr.status === 'normal' ? 'normal' : 'attention',
        statusLabel: !hr ? '无数据' : hr.status_label, trend: trends.hr,
      },
    ];
    (health?.personal_metrics || []).forEach((metric: PersonalMetric) => {
      const latest = metric.latest;
      rows.push({
        key: `personal-${metric.id}`,
        name: metric.name,
        unit: metric.unit,
        value: latest ? `${Number(latest.value).toFixed(Number.isInteger(latest.value) ? 0 : 1)}` : '--',
        target: metric.target_text || '持续观察',
        change: latest?.status_label || '待记录',
        recordedAt: formatTime(latest?.measured_at),
        dataSource: latest ? 'manual' : 'missing',
        sourceLabel: latest ? '手动记录' : '暂无来源',
        status: !latest ? 'missing' : latest.status === 'normal' ? 'normal' : latest.status === 'unknown' ? 'tracked' : 'attention',
        statusLabel: latest?.status_label || '无数据',
        trend: metric.trend.map((point) => Number(point.value)).filter(Number.isFinite),
        personalMetric: metric,
      });
    });
    return rows;
  }, [health, trends]);

  const coverage = useMemo(() => {
    const filled = metricRows.filter((row) => row.status !== 'missing').length;
    return metricRows.length ? Math.round((filled / metricRows.length) * 100) : 0;
  }, [metricRows]);

  const alerts = useMemo(() => {
    const result: Array<{ tone: 'warning' | 'critical'; title: string; detail: string; action: () => void }> = [];
    metricRows.filter((row) => row.status === 'attention').slice(0, 2).forEach((row) => {
      result.push({
        tone: 'warning',
        title: `${row.name}${row.statusLabel}`,
        detail: `最新值 ${row.value} ${row.unit}，建议结合连续趋势观察`,
        action: () => row.personalMetric ? setPersonalRecordMetric(row.personalMetric) : onOpenHealth('metrics'),
      });
    });
    const reportAbnormal = health?.latest_report?.abnormal?.[0];
    if (reportAbnormal && result.length < 2) {
      result.push({
        tone: 'warning',
        title: `体检报告：${reportAbnormal.name}${reportAbnormal.status === 'low' ? '偏低' : '偏高'}`,
        detail: `${reportAbnormal.value}${reportAbnormal.unit ? ` ${reportAbnormal.unit}` : ''}，参考 ${reportAbnormal.reference_range || '--'}`,
        action: () => onOpenHealth('reports'),
      });
    }
    const stale = metricRows.find((row) => {
      const age = row.recordedAt === '暂无数据' ? null : dataAgeDays(
        row.personalMetric?.latest?.measured_at
        || health?.[`latest_${row.key}`]?.measured_at
      );
      return row.status === 'missing' || age === null || age > 14;
    });
    if (stale && result.length < 2) {
      result.push({
        tone: 'critical',
        title: `${stale.name}数据需要更新`,
        detail: stale.recordedAt === '暂无数据' ? '尚未建立日常记录' : `最近记录为 ${stale.recordedAt}`,
        action: () => stale.personalMetric ? setPersonalRecordMetric(stale.personalMetric) : setRecordKind(stale.coreKind || 'weight'),
      });
    }
    return result;
  }, [health, metricRows, onOpenHealth]);

  const navigateWorkspace = (workspace: HealthWorkspace) => {
    setActiveWorkspace(workspace);
    if (workspace === 'metrics') onOpenHealth('metrics');
    if (workspace === 'reports') onOpenHealth('reports');
    if (workspace === 'nutrition') onOpenNutrition();
    if (workspace === 'fitness') onOpenFitness?.();
  };

  const calculateScore = async () => {
    setScoreBusy(true);
    try {
      await healthModulesApi.calculateScore();
      await load();
    } finally {
      setScoreBusy(false);
    }
  };

  const deletePersonalMetric = async (metric: PersonalMetric) => {
    if (!window.confirm(`删除“${metric.name}”及其全部记录？`)) return;
    await healthModulesApi.deletePersonalMetric(metric.id);
    await load();
  };

  if (loading) {
    return <div className="wl-root"><div className="wl-state"><span className="wl-loader" /><p>正在汇总身体数据...</p></div></div>;
  }

  const score = health?.score?.total;
  const hp = wellness?.hp?.hp ?? 0;
  const ep = wellness?.ep?.ep ?? 0;
  const riskCount = metricRows.filter((row) => row.status === 'attention').length + (health?.latest_report?.abnormal_count || 0);
  const report = health?.latest_report;

  return (
    <div className="wl-root">
      <header className="wl-topbar">
        <div className="wl-title-group">
          <button type="button" className="wl-icon-button wl-back-button" onClick={onClose} aria-label="返回"><ChevronLeft size={20} /></button>
          <div><h1>身体管理</h1><p>{formatSyncTime(latestTimestamp)}</p></div>
        </div>
        <div className="wl-top-actions">
          <button type="button" className="wl-add-metric-button" onClick={() => setAddMetricOpen(true)}><ClipboardPlus size={15} /><span>新增指标</span></button>
          <button type="button" className="wl-period-button" onClick={() => setPeriod((current) => current === '30' ? '7' : '30')}>{period}日</button>
          <button type="button" className="wl-icon-button" onClick={() => setRecordMenuOpen((current) => !current)} aria-label="更多记录选项"><MoreHorizontal size={18} /></button>
        </div>
      </header>

      <WellnessTabBar active={activeWorkspace} onNavigate={navigateWorkspace} />

      <main className="wl-main">
        {loadError && <div className="wl-error-banner">{loadError}<button type="button" onClick={load}>重试</button></div>}

        <section className="wl-summary-band" aria-label="身体状态摘要">
          <div className="wl-summary-metric primary">
            <span>综合健康评分</span>
            <strong>{score ?? '--'}{score != null && <small>/100</small>}</strong>
            <button type="button" onClick={calculateScore} disabled={scoreBusy}><RefreshCw size={11} />{scoreBusy ? '计算中' : score == null ? '生成评分' : '刷新评分'}</button>
          </div>
          <div className="wl-summary-metric">
            <span>需关注</span>
            <strong className={riskCount > 0 ? 'attention' : 'healthy'}>{riskCount > 0 ? `${riskCount}项` : '无明显风险'}</strong>
            <small>日常记录 + 体检报告</small>
          </div>
          <div className="wl-summary-metric">
            <span>日常数据覆盖</span>
            <strong className="blue">{coverage}<small>%</small></strong>
            <small>已记录 {metricRows.filter((row) => row.status !== 'missing').length}/{metricRows.length} 项</small>
          </div>
          <div className="wl-summary-metric compact"><span>HP</span><strong>{hp}<small>/100</small></strong><div className="wl-mini-progress"><i style={{ width: `${hp}%` }} /></div></div>
          <div className="wl-summary-metric compact"><span>EP</span><strong>{ep}<small>/100</small></strong><div className="wl-mini-progress blue"><i style={{ width: `${ep}%` }} /></div></div>
        </section>

        <div className="wl-dashboard-grid">
          <section className="wl-panel wl-monitor-panel">
            <header className="wl-section-header">
              <div><span className="wl-eyebrow">个人健康档案</span><h2>身体指标监测</h2></div>
              <div className="wl-section-actions">
                <button type="button" className="wl-text-button" onClick={() => setAddMetricOpen(true)}><Plus size={14} />自定义指标</button>
                <button type="button" className="wl-text-button" onClick={() => onOpenHealth('metrics')}>体检指标 ›</button>
              </div>
            </header>
            <div className="wl-monitor-table">
              <div className="wl-monitor-head"><span>指标</span><span>最新值</span><span>目标范围</span><span>{period}日状态</span><span>数据来源</span><span>趋势</span><span>状态</span></div>
              {metricRows.map((row) => (
                <div key={row.key} className="wl-monitor-row">
                  <button type="button" className="wl-row-main" onClick={() => row.personalMetric ? setPersonalRecordMetric(row.personalMetric) : setRecordKind(row.coreKind || 'weight')} aria-label={`记录${row.name}`} />
                  <span className="wl-metric-name"><StatusMark status={row.status} /><b>{row.name}</b><small>{row.unit || '无单位'}</small></span>
                  <span className="wl-metric-value">{row.value}</span>
                  <span>{row.target}</span>
                  <span className={row.status === 'attention' ? 'wl-attention-text' : row.status === 'normal' ? 'wl-positive-text' : ''}>{row.change}</span>
                  <span><b className={`wl-source-pill ${row.dataSource}`}>{row.sourceLabel}</b><small>{row.recordedAt}</small></span>
                  <span><Sparkline values={row.trend} tone={row.status === 'attention' ? 'amber' : row.status === 'normal' ? 'green' : 'blue'} /></span>
                  <span className="wl-row-status"><i className={`wl-status-chip ${row.status}`}>{row.statusLabel}</i>{row.personalMetric && <button type="button" className="wl-delete-metric" onClick={() => deletePersonalMetric(row.personalMetric!)} aria-label={`删除${row.name}`}><Trash2 size={13} /></button>}</span>
                </div>
              ))}
            </div>
            <p className="wl-table-note">点击任一指标即可补充记录。健康状态用于趋势管理，不替代医生诊断。</p>
          </section>

          <aside className="wl-side-column">
            <section className="wl-panel wl-report-panel">
              <header className="wl-section-header"><div><span className="wl-eyebrow">体检关联</span><h2>最新体检报告</h2></div><button type="button" className="wl-text-button" onClick={() => onOpenHealth('reports')}>全部报告 ›</button></header>
              {report ? (
                <div className="wl-report-content">
                  <button type="button" className="wl-report-summary" onClick={() => onOpenHealth('reports')}>
                    <span className="wl-report-icon"><FileText size={18} /></span>
                    <span><b>{report.title}</b><small>{report.report_date} · 已识别 {report.detected_count} 项</small></span>
                    <strong className={report.abnormal_count > 0 ? 'attention' : 'healthy'}>{report.abnormal_count > 0 ? `${report.abnormal_count}项异常` : '未见异常'}</strong>
                  </button>
                  {report.abnormal?.length > 0 && (
                    <div className="wl-report-abnormal">
                      {report.abnormal.slice(0, 3).map((item: any) => (
                        <button key={item.code || item.name} type="button" onClick={() => onOpenHealth('metrics')}>
                          <span><b>{item.name}</b><small>参考 {item.reference_range || '--'}</small></span>
                          <strong>{formatReportMetricValue(item)}</strong>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button type="button" className="wl-empty-action" onClick={() => onOpenHealth('reports')}><FileText size={20} /><span><b>尚未关联体检报告</b><small>上传 PDF 或图片，自动解析并同步到健康看板</small></span></button>
              )}
            </section>

            <section className="wl-panel">
              <header className="wl-section-header"><div><span className="wl-eyebrow">优先处理</span><h2>异常与数据提醒</h2></div></header>
              <div className="wl-alert-list">
                {alerts.length ? alerts.map((alert) => (
                  <button key={alert.title} type="button" className={`wl-alert-row ${alert.tone}`} onClick={alert.action}>
                    <span className="wl-alert-symbol"><AlertTriangle size={17} /></span>
                    <span><b>{alert.title}</b><small>{alert.detail}</small></span>
                    <em>处理</em>
                  </button>
                )) : <div className="wl-empty-row"><span><Check size={17} /></span><div><b>当前没有明显异常</b><small>继续保持稳定记录</small></div></div>}
              </div>
            </section>

            <section className="wl-panel">
              <header className="wl-section-header"><div><span className="wl-eyebrow">最近同步</span><h2>健康记录流</h2></div></header>
              <div className="wl-recent-list">
                {(health?.recent_records || []).length ? health.recent_records.slice(0, 5).map((record: any, index: number) => (
                  <div key={`${record.name}-${record.measured_at}-${index}`}>
                    <span className="wl-source-icon">{record.source === 'report' ? <FileText size={15} /> : <Activity size={15} />}</span>
                    <span><b>{record.name}</b><small>{formatTime(record.measured_at)} · {record.source === 'report' ? '体检报告' : '手动记录'}</small></span>
                    <strong>{record.value}<small>{record.unit}</small></strong>
                  </div>
                )) : <div className="wl-empty-row"><span><Gauge size={17} /></span><div><b>还没有健康记录</b><small>从一次血压或体重记录开始</small></div></div>}
              </div>
            </section>

            <section className="wl-panel">
              <header className="wl-section-header"><div><span className="wl-eyebrow">数据来源</span><h2>看板完整度</h2></div></header>
              <div className="wl-source-list">
                <div><span className="wl-source-icon"><Activity size={16} /></span><span><b>日常手动记录</b><small>{health?.data_summary?.manual_metric_count || 0} 项核心数据</small></span><em className="ok">可编辑</em></div>
                <button type="button" onClick={() => onOpenHealth('reports')}><span className="wl-source-icon"><FileText size={16} /></span><span><b>体检报告</b><small>{health?.data_summary?.report_metric_count || 0} 项已识别</small></span><em className={report ? 'ok' : 'stale'}>{report ? '已关联' : '未关联'}</em></button>
                <div><span className="wl-source-icon"><ClipboardPlus size={16} /></span><span><b>个人自定义指标</b><small>{health?.data_summary?.personal_metric_count || 0} 项正在追踪</small></span><em className="ok">可扩展</em></div>
                <div><span className="wl-source-icon"><Watch size={16} /></span><span><b>穿戴设备</b><small>睡眠与活动自动同步</small></span><em>规划中</em></div>
              </div>
            </section>

            {health?.goals?.length > 0 && (
              <section className="wl-panel">
                <header className="wl-section-header"><div><span className="wl-eyebrow">追踪进度</span><h2>健康目标</h2></div><button type="button" className="wl-text-button" onClick={onOpenMissions}>任务中心 ›</button></header>
                <div className="wl-goal-list">
                  {health.goals.slice(0, 3).map((goal: any) => <div key={goal.id} className="wl-goal-row"><span><b>{goal.type_label}</b><small>{goal.deadline ? `截止 ${goal.deadline}` : '持续追踪'}</small></span><strong>{goal.target_value}{goal.unit}</strong></div>)}
                </div>
              </section>
            )}
          </aside>
        </div>
      </main>

      <div className="wl-primary-action">
        <button type="button" onClick={() => setRecordMenuOpen((current) => !current)}><Plus size={20} />记录健康数据</button>
      </div>

      {recordMenuOpen && (
        <>
          <button type="button" className="wl-record-picker-backdrop" aria-label="关闭记录菜单" onClick={() => setRecordMenuOpen(false)} />
          <div className="wl-record-picker" aria-label="选择记录类型">
            {CORE_RECORD_OPTIONS.map((option) => <button key={option.kind} type="button" onClick={() => { setRecordKind(option.kind); setRecordMenuOpen(false); }}>{option.label}</button>)}
            {(health?.personal_metrics || []).map((metric: PersonalMetric) => <button key={metric.id} type="button" onClick={() => { setPersonalRecordMetric(metric); setRecordMenuOpen(false); }}>{metric.name}</button>)}
            <button type="button" className="wl-picker-add" onClick={() => { setAddMetricOpen(true); setRecordMenuOpen(false); }}><Plus size={14} />新增指标</button>
          </div>
        </>
      )}

      {recordKind && <HealthRecordDialog
        kind={recordKind}
        onClose={() => setRecordKind(null)}
        onSaved={async () => { await load(); }}
        onOpenHistory={(kind) => { setRecordKind(null); setHistoryKind(kind); }}
      />}
      {historyKind && <VitalHistoryDialog
        initialKind={historyKind}
        onClose={() => setHistoryKind(null)}
        onRecord={(kind) => { setHistoryKind(null); setRecordKind(kind); }}
      />}
      {personalRecordMetric && <PersonalRecordDialog metric={personalRecordMetric} onClose={() => setPersonalRecordMetric(null)} onSaved={load} />}
      {addMetricOpen && <AddMetricDialog templates={templates} onClose={() => setAddMetricOpen(false)} onSaved={load} />}
    </div>
  );
}

export default WellnessCenter;
